import React, { useState } from 'react';
import {
  FaArrowUp, FaArrowDown, FaWrench, FaTimes, FaPlane, FaBuilding,
  FaUser, FaGasPump, FaTruck, FaStickyNote,
  FaCheckCircle, FaRegCircle, FaCamera, FaExpand, FaTachometerAlt
} from 'react-icons/fa';

export function fmtTsLong(ts) {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    + '  ·  '
    + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

const STEPS_OUT = [
  'Collected aircraft keys',
  'Collected fuel sample glass',
  'Stored drain glass',
  'Moved bowser to hangar',
  'Connected ground cable (bowser → aircraft)',
  'Extended hose to ramp',
  'Connected ground cable (nozzle → aircraft)',
  'Scanned aircraft QR',
  'Scanned fuel pump QR',
  'Recorded meter before',
  'Took photo of meter (before)',
  'Recorded meter after',
  'Took photo of meter (after)',
  'Disconnected fuel nozzle from aircraft',
  'Disconnected ground cable (nozzle)',
  'Collected hose',
  'Closed fuel cap',
  'Disconnected ground cable (bowser)',
];

const STEPS_IN = [
  'Scanned tank QR',
  'Selected vendor',
  'Entered amount fueled',
  'Took receipt photo',
];

function StepsList({ steps, checks }) {
  return (
    <div className="txn-steps-list">
      {steps.map((label, i) => {
        const done = checks[i];
        return (
          <div key={i} className={`txn-step-row ${done ? 'done' : 'pending'}`}>
            <span className="txn-step-icon">
              {done ? <FaCheckCircle /> : <FaRegCircle />}
            </span>
            <span className="txn-step-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DetailRow({ icon, label, value, strong }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="txn-detail-row">
      <div className="txn-detail-row-label">
        <span className="txn-detail-row-icon">{icon}</span>
        {label}
      </div>
      <div className="txn-detail-row-value">
        {strong ? <strong>{value}</strong> : value}
      </div>
    </div>
  );
}

function PhotoGallery({ photos }) {
  const [lightbox, setLightbox] = useState(null);
  const entries = photos.filter(p => p.url);
  if (!entries.length) return null;
  return (
    <>
      <div className="txn-photo-gallery">
        {entries.map(({ label, url }) => (
          <div key={label} className="txn-photo-card" onClick={() => setLightbox({ label, url })}>
            <img src={url} alt={label} className="txn-photo-img" />
            <div className="txn-photo-label">
              <FaCamera style={{ marginRight: 5 }} />{label}
            </div>
            <div className="txn-photo-expand"><FaExpand /></div>
          </div>
        ))}
      </div>
      {lightbox && (
        <div className="txn-lightbox" onClick={() => setLightbox(null)}>
          <div className="txn-lightbox-inner" onClick={e => e.stopPropagation()}>
            <div className="txn-lightbox-header">
              <span>{lightbox.label}</span>
              <button className="modal-close-btn" onClick={() => setLightbox(null)}><FaTimes /></button>
            </div>
            <img src={lightbox.url} alt={lightbox.label} className="txn-lightbox-img" />
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <div className="txn-section">
      <div className="txn-section-title">{title}</div>
      {children}
    </div>
  );
}

export default function TxnDetailModal({ tx, onClose }) {
  if (!tx) return null;

  const isOut  = tx.type === 'OUT';
  const isIn   = tx.type === 'IN';
  const typeLabel = isOut ? 'Fuel-OUT' : isIn ? 'Fuel-IN' : 'Correction';
  const typeClass = tx.type?.toLowerCase();

  // Derive which steps were completed based on available data
  const checksOut = [
    true, true,       // keys + sample (step 1 — prerequisite, always done if submitted)
    true, true,       // stored glass + moved bowser (step 2)
    true, true, true, // cables + hose (step 2 cont)
    !!tx.aircraft,    // scanned aircraft
    !!tx.tanker,      // scanned tanker
    true,             // meter before (required to submit)
    !!tx.photo_before,
    true,             // meter after (required to submit)
    !!tx.photo_after,
    true, true, true, true, true, // step 9 checklist (always done if submitted)
  ];

  const checksIn = [
    !!tx.tanker,
    !!tx.vendor,
    true,               // amount (required to submit)
    !!tx.photo_receipt,
  ];

  const photos = isOut
    ? [{ label: 'Meter — Before', url: tx.photo_before }, { label: 'Meter — After', url: tx.photo_after }]
    : isIn
    ? [{ label: 'Receipt', url: tx.photo_receipt }]
    : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-lg txn-detail-modal" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className={`txn-type-badge ${typeClass}`} style={{ fontSize: 13, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
              {isOut ? <FaArrowUp /> : isIn ? <FaArrowDown /> : <FaWrench />}
              {typeLabel}
            </span>
            <div>
              <h2 className="modal-title">Transaction #{tx.id}</h2>
              <div className="txn-detail-subtitle">{fmtTsLong(tx.timestamp)}</div>
            </div>
          </div>
          <button className="modal-close-btn" onClick={onClose}><FaTimes /></button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body txn-detail-body">

          {/* Left column */}
          <div className="txn-detail-col txn-detail-col-main">

            {/* Core info */}
            <Section title="Summary">
              <div className="txn-detail-grid">
                <DetailRow icon={<FaTruck />}        label="Tanker"            value={tx.tanker || '—'} />
                <DetailRow icon={<FaGasPump />}      label="Fuel Type"         value={tx.gas_type || '—'} />
                <DetailRow icon={null}               label={isOut ? 'Amount Dispensed' : 'Amount Added'}  value={`${tx.amount} ${tx.amount_type}`} strong />
                {isOut && <DetailRow icon={<FaPlane />}    label="Aircraft"    value={tx.aircraft || '—'} />}
                {isIn  && <DetailRow icon={<FaBuilding />} label="Vendor"      value={tx.vendor   || '—'} />}
                <DetailRow icon={<FaUser />}         label="Fueler"            value={tx.fueler || 'Admin'} />
                <div className="txn-detail-row">
                  <div className="txn-detail-row-label"><span className="txn-detail-row-icon" />Status</div>
                  <div className="txn-detail-row-value">
                    <span className="status-dot active" style={{ textTransform: 'capitalize' }}>{tx.status || 'completed'}</span>
                  </div>
                </div>
              </div>
              {tx.notes && (
                <div className="txn-detail-notes">
                  <div className="txn-detail-row-label"><FaStickyNote /> Notes</div>
                  <p className="txn-detail-notes-text">{tx.notes}</p>
                </div>
              )}
            </Section>

            {/* Meter readings — OUT and IN */}
            {(tx.meter_before != null || tx.meter_after != null) && (
              <Section title={isOut ? 'Meter Readings' : 'Tank Stock Levels'}>
                <div className="txn-meter-row">
                  <div className="txn-meter-card txn-meter-before">
                    <div className="txn-meter-icon"><FaTachometerAlt /></div>
                    <div className="txn-meter-label">{isOut ? 'Before Fueling' : 'Stock Before'}</div>
                    <div className="txn-meter-value">
                      {tx.meter_before != null ? `${tx.meter_before} ${tx.meter_unit || tx.amount_type}` : '—'}
                    </div>
                  </div>
                  <div className="txn-meter-arrow">→</div>
                  <div className="txn-meter-card txn-meter-after">
                    <div className="txn-meter-icon"><FaTachometerAlt /></div>
                    <div className="txn-meter-label">{isOut ? 'After Fueling' : 'Stock After'}</div>
                    <div className="txn-meter-value">
                      {tx.meter_after != null ? `${tx.meter_after} ${tx.meter_unit || tx.amount_type}` : '—'}
                    </div>
                  </div>
                </div>
                {tx.meter_before != null && tx.meter_after != null && (
                  <div className="txn-meter-diff">
                    {isOut
                      ? <>Difference: <strong>{Math.abs(tx.meter_after - tx.meter_before).toFixed(2)} {tx.meter_unit || tx.amount_type}</strong>
                          {' '}&nbsp;·&nbsp; Recorded amount: <strong>{tx.amount} {tx.amount_type}</strong></>
                      : <>Stock gain: <strong>+{Math.abs(tx.meter_after - tx.meter_before).toFixed(2)} {tx.meter_unit || tx.amount_type}</strong>
                          {' '}&nbsp;·&nbsp; Received: <strong>{tx.amount} {tx.amount_type}</strong></>
                    }
                  </div>
                )}
              </Section>
            )}

            {/* Photos */}
            {photos.some(p => p.url) && (
              <Section title="Photos">
                <PhotoGallery photos={photos} />
              </Section>
            )}
            {photos.length > 0 && !photos.some(p => p.url) && (
              <Section title="Photos">
                <p className="txn-no-photos">No photos were submitted with this transaction.</p>
              </Section>
            )}
          </div>

          {/* Right column — steps */}
          {(isOut || isIn) && (
            <div className="txn-detail-col txn-detail-col-steps">
              <Section title={`Process Steps (${isOut ? 'Fuel-OUT' : 'Fuel-IN'})`}>
                <StepsList
                  steps={isOut ? STEPS_OUT : STEPS_IN}
                  checks={isOut ? checksOut : checksIn}
                />
              </Section>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
